import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { FlightTrackerService } from '@core/flight-tracker/flight-tracker.service';
import { convertToDate } from '@core/utils/date.utils';
import { WindowService } from '@core/window-actions';
import { FLIGHT_TRACKER_CONFIG } from '@dynamic-components/flight-tracker/flight-tracker.config';
import { FlightTrackerCopy } from '@dynamic-components/flight-tracker/types/flight-tracker-copy.type';
import { FlightTrackerLeg } from '@dynamic-components/flight-tracker/types/flight-tracker-leg.type';
import { memo } from '@shared/ui';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import { JbDialogService } from 'jb-component-library';

import { FlightTrackerResultsService } from '../flight-tracker-results.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'jb-flight-tracker-results-dialog',
  templateUrl: './flight-tracker-results-dialog.component.html',
  styleUrls: ['./flight-tracker-results-dialog.component.scss'],
  providers: [FlightTrackerResultsService, FlightTrackerService],
})
export class FlightTrackerResultsDialogComponent {
  leg;
  departureDate: any;
  flightTrackerConnectingResults: FlightTrackerLeg;
  timeDepartMap: any;
  dateDepartMap: any;
  timeArrivalMap: any;
  dateArrivalMap: any;
  config = FLIGHT_TRACKER_CONFIG;
  URL: string;
  secondLegURL: string;
  dialogSecondNewArrival: any;
  dialogSecondScheduledArrival: any;
  dialogSecondNewDeparture: any;
  dialogSecondScheduledDeparture: any;
  dialogSecondDestinationGate: any;
  dialogSecondDestinationTerm: any;
  dialogSecondDestination: any;
  dialogSecondOriginGate: any;
  dialogSecondOriginTerminal: any;
  dialogSecondOrigin: any;
  dialogSecondStatus: any;
  dialogSecondFlightNo: any;
  dialogNewArrival: any;
  dialogScheduledArrival: any;
  dialogNewDeparture: any;
  dialogScheduledDeparture: any;
  dialogDestinationGate: any;
  dialogDestinationTerminal: any;
  dialogDestination: any;
  dialogOriginGate: any;
  dialogOriginTerminal: any;
  dialogOrigin: any;
  dialogStatus: any;
  dialogFlightNo: any;
  dialogScheduledOriginAirport: any;
  dialogSecondScheduledOriginAirport: any;
  dialogScheduledDestinationAirport: any;
  dialogSecondScheduledDestinationAirport: any;
  isMobile: boolean;
  currentTime = new Date();
  refreshing = false;
  originAndDestinationAirport: { origin: string; destination: string };
  secondOriginAndDestinationAirport: { origin: string; destination: string };

  flightTrackerText: FlightTrackerCopy;
  private _destroy = inject(DestroyRef);

  constructor(
    private windowService: WindowService,
    private flightTrackerResultsService: FlightTrackerResultsService,
    private flightTrackerService: FlightTrackerService,
    public dialogService: JbDialogService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.config = FLIGHT_TRACKER_CONFIG;
    this.windowService.windowSizeChanged.subscribe(size => {
      this.isMobile = !!size.small;
    });
  }

  @memo()
  convertTime(time: string) {
    return this.flightTrackerResultsService.convertTime(time);
  }

  @memo()
  setStatusColor(status: string): string {
    return this.flightTrackerResultsService.setStatusColor(status);
  }

  calculateRemainingTime(time: string) {
    return this.flightTrackerResultsService.calculateRemainingTime(time);
  }

  isInFlight(departure: string, arrival: string) {
    if (
      isAfter(this.currentTime, parseISO(departure)) &&
      isBefore(this.currentTime, parseISO(arrival))
    ) {
      return true;
    } else {
      return false;
    }
  }

  closeDialog() {
    this.dialogService.closeDialog();
  }

  handleData = (
    departureDate: any,
    flightTrackerCopy: FlightTrackerCopy,
    result: FlightTrackerLeg,
    flightTrackerConnectingResults?: FlightTrackerLeg,
  ) => {
    this.flightTrackerText = flightTrackerCopy;
    this.departureDate = departureDate;
    this.leg = result;
    this.dialogFlightNo = result.flightNo;
    this.dialogStatus = result.flightStatus;
    this.dialogOrigin = result.originAirport;
    this.dialogOriginTerminal = !result.originTerminal
      ? '-'
      : result.originTerminal;
    this.dialogOriginGate = !result.originGate ? '-' : result.originGate;
    this.dialogDestination = result.destinationAirport;
    this.dialogDestinationTerminal = !result.destinationTerminal
      ? '-'
      : result.destinationTerminal;
    this.dialogDestinationGate = !result.destinationGate
      ? '-'
      : result.destinationGate;
    this.dialogScheduledDeparture = result.scheduledDeparture;
    this.dialogNewDeparture = result.actualDeparture;
    this.dialogScheduledArrival = result.scheduledArrival;
    this.dialogNewArrival = result.actualArrival;
    this.dialogScheduledOriginAirport = result.scheduledOriginAirport;
    this.dialogScheduledDestinationAirport = result.scheduledDestinationAirport;
    this.dateArrivalMap = format(
      convertToDate(result.actualArrival),
      'yyyy/MM/dd',
    );
    this.dateDepartMap = format(
      convertToDate(result.actualDeparture),
      'yyyy/MM/dd',
    );
    if (result.actualArrival && result.actualDeparture) {
      this.timeArrivalMap = result.actualArrival.slice(11, 19);
      this.timeDepartMap = result.actualDeparture.slice(11, 19);
    }
    this.URL = `${
      this.config.FLIGHT_MAP.PROD +
      this.config.FLIGHT_MAP.MIDDLEPARAMS +
      this.config.FLIGHT_MAP.DEPARTURE
    }=${this.dialogOrigin}&${this.config.FLIGHT_MAP.FLIGHTNO}=${
      this.dialogFlightNo
    }&${this.config.FLIGHT_MAP.ETA}=${this.dateArrivalMap}, ${
      this.timeArrivalMap
    }&${this.config.FLIGHT_MAP.DEPARTURETIME}=${this.dateDepartMap}, ${
      this.timeDepartMap
    }`;
    this.flightTrackerConnectingResults = flightTrackerConnectingResults;
    if (this.flightTrackerConnectingResults) {
      this.dialogSecondFlightNo = this.flightTrackerConnectingResults.flightNo;
      this.dialogSecondStatus =
        this.flightTrackerConnectingResults.flightStatus;
      this.dialogSecondOrigin =
        this.flightTrackerConnectingResults.originAirport;
      this.dialogSecondOriginTerminal = !this.flightTrackerConnectingResults
        .originTerminal
        ? '-'
        : this.flightTrackerConnectingResults.originTerminal;
      this.dialogSecondOriginGate = !this.flightTrackerConnectingResults
        .originGate
        ? '-'
        : this.flightTrackerConnectingResults.originGate;
      this.dialogSecondScheduledOriginAirport =
        this.flightTrackerConnectingResults.scheduledOriginAirport;
      this.dialogSecondDestination =
        this.flightTrackerConnectingResults.destinationAirport;
      this.dialogSecondDestinationTerm = !this.flightTrackerConnectingResults
        .destinationTerminal
        ? '-'
        : this.flightTrackerConnectingResults.destinationTerminal;
      this.dialogSecondDestinationGate = !this.flightTrackerConnectingResults
        .destinationGate
        ? '-'
        : this.flightTrackerConnectingResults.destinationGate;
      this.dialogSecondScheduledDestinationAirport =
        this.flightTrackerConnectingResults.scheduledDestinationAirport;
      this.dialogSecondScheduledDeparture =
        this.flightTrackerConnectingResults.scheduledDeparture;
      this.dialogSecondNewDeparture =
        this.flightTrackerConnectingResults.actualDeparture;
      this.dialogSecondScheduledArrival =
        this.flightTrackerConnectingResults.scheduledArrival;
      this.dialogSecondNewArrival =
        this.flightTrackerConnectingResults.actualArrival;
      this.secondLegURL = `${
        this.config.FLIGHT_MAP.PROD +
        this.config.FLIGHT_MAP.MIDDLEPARAMS +
        this.config.FLIGHT_MAP.DEPARTURE
      }=${this.dialogSecondOrigin}&${this.config.FLIGHT_MAP.FLIGHTNO}=${
        this.dialogSecondFlightNo
      }&${this.config.FLIGHT_MAP.ETA}=${this.dateArrivalMap}, ${
        this.timeArrivalMap
      }&${this.config.FLIGHT_MAP.DEPARTURETIME}=${this.dateDepartMap}, ${
        this.timeDepartMap
      }`;
    } else {
      this.dialogSecondFlightNo = '';
    }
    this.currentTime = new Date();
    this.originAndDestinationAirport = {
      origin: this.dialogOrigin || '',
      destination: this.dialogDestination || '',
    };
    this.secondOriginAndDestinationAirport = {
      origin: this.dialogSecondOrigin || '',
      destination: this.dialogSecondDestination || '',
    };
    this.cdRef.markForCheck();
  };

  refreshFlightData(): void {
    this.refreshing = true;
    if (!this.flightTrackerConnectingResults) {
      this.flightTrackerService
        .getFlightStatusByFlight(this.departureDate, this.dialogFlightNo)
        .subscribe(res => {
          this.handleData(
            this.departureDate,
            this.flightTrackerText,
            res.directFlightResults[0]?.legs[0],
          );
          this.refreshing = false;
        });
    } else {
      this.flightTrackerService
        .getFlightStatusByRoute(
          this.departureDate,
          this.dialogOrigin,
          this.dialogSecondDestination,
        )
        .subscribe(res => {
          const currentFlight = res.connectingFlightResults.filter(
            flight => flight.legs[0].flightNo === this.dialogFlightNo,
          )[0];
          this.handleData(
            this.departureDate,
            this.flightTrackerText,
            currentFlight.legs[0],
            currentFlight.legs[1],
          );
          this.refreshing = false;
        });
    }
  }
}
